import React, { useEffect, useState } from 'react';

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { v4 as uuidv4 } from 'uuid';

// Create a new axios instance with retry configuration (copied from Review.js)
const axiosInstance = axios.create();
axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: (retryCount) => {
    return retryCount * 1000; // wait 1s, 2s, 3s between retries
  },
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || (error.response && error.response.status >= 500);
  },
});


function TestPage({ formData, setFormData, attachments, setAttachments }) { // Added attachments prop
  const [logs, setLogs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // State to disable button during submission

  // Function to add log messages
  const addLog = (message) => {
    setLogs(prevLogs => [...prevLogs, `${new Date().toISOString()}: ${message}`]);
  };

  useEffect(() => {
    addLog('TestPage component mounted.');
    // Simulate pre-filling form data
    addLog('Pre-filling form data for testing in the expected structure.');
    // Structure formData similar to App.js state
    setFormData({
      companyName: 'Test Company Inc.', // Using expected field name
      contactName: 'Test User',         // Using expected field name
      contactEmail: 'test@example.com', // Using expected field name
      answers: [ // Use the answers array structure
        { text: 'Test Answer 1: Project Description - This is a test project description.', attachments: [] },
        { text: 'Test Answer 2: Placeholder', attachments: [] },
        { text: 'Test Answer 3: Placeholder', attachments: [] },
        { text: 'Test Answer 4: Placeholder', attachments: [] },
        { text: 'Test Answer 5: Placeholder', attachments: [] }
      ]
    });

    // Add sample attachments (will be associated with answers later in submit logic)
    addLog('Adding 5 sample attachments for testing.');
    setAttachments([
      new File(["sample content 1"], "sample1.txt", { type: "text/plain" }),
      new File(["sample content 2"], "sample2.pdf", { type: "application/pdf" }),
      new File(["sample content 3"], "sample3.docx", { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }),
      new File(["sample content 4"], "sample4.jpg", { type: "image/jpeg" }),
      new File(["sample content 5"], "sample5.zip", { type: "application/zip" }),
    ]);

  }, [setFormData, setAttachments]); // Dependencies ensure this runs once on mount

  // Function to handle the REAL backend submission
  const handleRealSubmit = async () => {
    setIsSubmitting(true);
    addLog('--- Starting Real Backend Submission ---');

    // Basic validation (can be expanded)
    if (!formData.companyName || !formData.contactName || !formData.contactEmail) {
        addLog('ERROR: Missing required company information in prefilled data.');
        setIsSubmitting(false);
        return;
    }
     if (!attachments || attachments.length === 0) {
        addLog('WARNING: No sample attachments found in state.');
        // Decide if this is an error or just a warning for the test page
    }


    const submissionData = new FormData();
    const uuid = uuidv4();
    const inviteCode = 'TEST2025'; // Hardcode the test invite code

    addLog(`Generated UUID: ${uuid}`);
    addLog(`Using Invite Code: ${inviteCode}`);

    // Add metadata
    submissionData.append('uuid', uuid);
    submissionData.append('inviteCode', inviteCode);
    submissionData.append('companyName', formData.companyName);
    submissionData.append('contactName', formData.contactName);
    submissionData.append('contactEmail', formData.contactEmail);
    addLog('Appended metadata to FormData.');

    // Prepare answers JSON according to API Guide (simple structure)
    const answersJson = formData.answers.map((answer, index) => {
      // The API guide expects a simple structure without embedded markdown or attachment counts.
      // Files are handled separately by the `attachment_qX_Y` fields.
      addLog(`Preparing answer for Question ${index + 1}`);
      return {
        questionId: index + 1, // Corrected: Start questionId from 1
        answer: answer.text // Use only the raw answer text
        // Do not include attachmentCount or markdown links here
      };
    });
    
    const answersJsonString = JSON.stringify(answersJson);
    submissionData.append('answers', answersJsonString);
    addLog('Prepared and appended answers JSON:');
    addLog(answersJsonString);


    // Add attachments with the naming convention: attachment_q{questionId}_{fileIndex}
    // Associate one mock attachment per mock question for this test.
    if (attachments.length > 0 && formData.answers.length === attachments.length) {
        formData.answers.forEach((answer, index) => {
            const questionId = index + 1; // Use 1-based question ID
            const file = attachments[index]; // Get the corresponding mock file
            const fileIndex = 0; // Assuming one file per question in this test setup
            const attachmentKey = `attachment_q${questionId}_${fileIndex}`;
            submissionData.append(attachmentKey, file, file.name);
            addLog(`Appended attachment for Question ${questionId}: Key='${attachmentKey}', Name='${file.name}', Type='${file.type}'`);
        });
    } else if (attachments.length > 0) {
         addLog(`WARNING: Mismatch between number of answers (${formData.answers.length}) and attachments (${attachments.length}). Not attaching files.`);
    }
    else {
        addLog('No attachments to append.');
    }


    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const apiUrl = `${apiBase}/api/submit`;

    addLog(`API Endpoint: ${apiUrl}`);

    const headers = {
      'Content-Type': 'multipart/form-data',
    };

    addLog('Request Headers:');
    addLog(JSON.stringify(headers, null, 2));
    addLog('--- Sending Request ---');

    try {
      const response = await axiosInstance.post(apiUrl, submissionData, { headers });

      addLog('--- Received Response ---');
      addLog(`Status: ${response.status} ${response.statusText}`);
      addLog('Response Headers:');
      addLog(JSON.stringify(response.headers, null, 2));
      addLog('Response Body:');
      addLog(JSON.stringify(response.data, null, 2));

      if (response.status === 200 && response.data.success) {
        addLog('SUCCESS: Submission successful!');
        // Optionally navigate or show success message
        // navigate('/thank-you'); // Example navigation
      } else {
        addLog(`WARNING: Submission response indicates potential issue. Status: ${response.status}, Success Flag: ${response.data.success}`);
        addLog(`Server Message: ${response.data.message || 'No message'}`);
      }
    } catch (error) {
       addLog('--- ERROR During Submission ---');
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        addLog(`Error Status: ${error.response.status} ${error.response.statusText}`);
        addLog('Error Response Headers:');
        addLog(JSON.stringify(error.response.headers, null, 2));
        addLog('Error Response Body:');
        addLog(JSON.stringify(error.response.data, null, 2));
        const errorData = error.response.data;
         if (errorData && errorData.success === false) {
             const errorCode = errorData.error || 'Unknown error';
             const errorMessage = errorData.message || 'No detailed message provided';
             addLog(`API Error Code: ${errorCode}`);
             addLog(`API Error Message: ${errorMessage}`);
         }
      } else if (error.request) {
        // The request was made but no response was received
        addLog('Error: No response received from server.');
        addLog(JSON.stringify(error.request, null, 2));
      } else {
        // Something happened in setting up the request that triggered an Error
        addLog('Error setting up the request:');
        addLog(error.message);
      }
       addLog('Full Error Object:');
       addLog(JSON.stringify(error, Object.getOwnPropertyNames(error), 2)); // Log non-enumerable properties too
    } finally {
      addLog('--- Submission Attempt Finished ---');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Backend Integration Test Page</h2>

      <div className="mb-4">
        <h3 className="text-xl font-semibold mb-2">Actions</h3>
        <button
          onClick={handleRealSubmit} // Use the real submit handler
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-2"
          disabled={isSubmitting} // Disable button while submitting
        >
          {isSubmitting ? 'Submitting...' : 'Submit Real Data'}
        </button>
         {/* Add more buttons for other test actions if needed */}
      </div>

      <div className="mb-4">
          <h3 className="text-xl font-semibold mb-2">Form Data (Prefilled)</h3>
          <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
            {JSON.stringify(formData, null, 2)}
          </pre>
      </div>

      <div className="mb-4">
        <h3 className="text-xl font-semibold mb-2">Logs</h3>
        <div className="bg-gray-900 text-white p-4 rounded h-64 overflow-y-auto font-mono text-sm">
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TestPage;
